import { useState } from 'react';
import { C } from '@ridgewill/brand';
import { Select } from '../components/Select.js';
import { TicketPrefixField } from '../components/TicketPrefixField.js';
import { cleanTicketPrefix, ticketPrefixProblem } from '../lib/ticket-prefix.js';
import { adminApi, type HealthReport } from '../lib/api.js';
import { useAsync, useAction } from '../lib/useAsync.js';
import { useChips } from '../lib/useChips.js';
import { count } from '../lib/format.js';
import { formatPlatforms } from '../lib/platforms.js';
import {
  ChipBar,
  FilterPills,
  Menu,
  MenuItem,
  PrimaryButton,
  Screen,
  inputStyle,
} from '../components/Screen.js';
import { useCan, whyDisabled } from '../lib/capabilities.js';
import {
  AsyncBoundary,
  Badge,
  Button,
  EmptyState,
  Field,
  OutcomeNote,
  Panel,
  PlatformGrid,
  Platforms,
  Table,
  Td,
  When,
} from '../components/ui.js';
import { Modal, ModalActions } from '../components/Modal.js';
import {
  ApplicationDetail,
  HealthState,
  GrantCheckboxes,
  STATUSES,
} from './ApplicationDetail.js';
import { StatusPill } from './Overview.js';
import { needsClosureHook } from '../lib/closure-config.js';

/**
 * The application registry — the screen that makes this a platform portal.
 *
 * Registering an app used to mean editing an `APPS` constant in the auth service
 * and deploying it. Apps and their role grants are rows now, so this screen is
 * what edits them, and shipping a new app no longer waits on a release.
 *
 * ── The health check is a button, and never a fetch on mount ─────────
 *
 * Each run makes real outbound requests from the admin deployment. A check that
 * fired when the screen opened would make every visit — every reload, every
 * absent-minded refresh — a round of live traffic to every registered app. So
 * it is an action with a result, and the result is held for this visit only.
 *
 * The column DISAPPEARS when nothing is configured, rather than rendering a row
 * of identical grey placeholders that look like a broken feature.
 */
export function Applications({ id, tab }: { id?: string; tab?: string }) {
  /* Keyed, so moving between two applications remounts rather than leaving the
     previous one's half-edited Settings form behind under a new name. */
  return id ? <ApplicationDetail key={id} id={id} tab={tab} /> : <ApplicationList />;
}

function ApplicationList() {
  const [status, setStatus] = useState<string>('all');
  const search = useChips();
  const [health, setHealth] = useState<HealthReport>();
  const [adding, setAdding] = useState(false);
  const mayWrite = useCan('application:write');
  const check = useAction();

  const { state, reload } = useAsync(
    () =>
      adminApi.applications({
        status: status === 'all' ? undefined : status,
        terms: search.active,
      }),
    [search.key, status],
  );

  const results = new Map((health?.results ?? []).map((r) => [r.id, r]));

  return (
    <Screen
      filters={
        <FilterPills
          label="Filter by status"
          current={status}
          onSelect={setStatus}
          options={[
            { value: 'all', label: 'All' },
            { value: 'live', label: 'Live' },
            { value: 'beta', label: 'Beta' },
            { value: 'alpha', label: 'Alpha' },
            { value: 'in-dev', label: 'In dev' },
            { value: 'planning', label: 'Planning' },
          ]}
        />
      }
      actions={
        <>
          {health ? (
            <span style={{ fontSize: '.8rem', color: C.muted }}>
              {summarise(health)} · <When iso={health.checkedAt} />
            </span>
          ) : null}
          <Button
            disabled={check.running}
            onClick={() =>
              void check.run(async () => {
                const report = await adminApi.checkHealth();
                setHealth(report);
                return report.configured === 0
                  ? 'No application has a health check URL configured.'
                  : summarise(report);
              })
            }
          >
            {check.running ? 'Checking…' : 'Check health'}
          </Button>
          <PrimaryButton
            disabled={!mayWrite}
            title={mayWrite ? undefined : whyDisabled('registering applications')}
            onClick={() => setAdding(true)}
          >
            + Add application
          </PrimaryButton>
        </>
      }
      chips={
        <ChipBar
          search={search}
          placeholder="Search name, slug, description or platform…"
          appliedByServer={state.status === 'ready' ? state.data.terms : undefined}
        />
      }
    >
      <OutcomeNote outcome={check.outcome} />

      <Panel title="Applications">
        <AsyncBoundary state={state}>
          {(d) => {
            /** The column exists only if anything can fill it. */
            const anyHealth = d.applications.some((a) => a.healthCheckUrl);

            return (
              <>
                {d.applications.length === 0 ? (
                  <EmptyState
                    title={
                      search.active.length > 0 || status !== 'all'
                        ? 'No applications match'
                        : 'No applications registered'
                    }
                    suggestion={
                      search.active.length > 0 || status !== 'all'
                        ? 'Try a different term, or switch the status filter back to All.'
                        : 'Register one to grant a security group access to it.'
                    }
                  />
                ) : (
                  <Table
                    columns={[
                      'Application',
                      'Platform',
                      'Status',
                      'Reachable by',
                      ...(anyHealth ? ['Health'] : []),
                      '',
                    ]}
                  >
                    {d.applications.map((a) => (
                      <tr key={a.id} className="rw-rowhover" style={{ opacity: a.active ? 1 : 0.55 }}>
                        <Td>
                          {/* An anchor since the application became a screen —
                              same reasoning as the Users list. */}
                          <a href={`#/applications/${encodeURIComponent(a.slug)}`} style={rowLink}>
                            {a.name}
                          </a>
                          <div style={{ color: C.muted, fontSize: '.78rem' }}>{a.slug}</div>
                        </Td>
                        <Td>
                          <Platforms value={a.platform} />
                        </Td>
                        <Td>
                          <span style={{ display: 'inline-flex', gap: '.4rem', alignItems: 'center' }}>
                            <StatusPill status={a.status} />
                            {a.active ? null : <Badge tone="danger">retired</Badge>}
                            {/* Same reasoning as the "nobody" badge next door: a
                                legitimate state that looks identical to a working
                                one. An app with no closure webhook is never told
                                when somebody closes their Ridgewill identity, so
                                it keeps that person's data — and nothing fails to
                                say so. The row is the only place it can surface.

                                Only for ACTIVE apps: retiring one does not delete
                                what it holds, but a retired app has a louder badge
                                already and two warnings on one row reads as noise.

                                And only for apps that HOLD something. Our own
                                portals are reachable by everybody and store
                                nothing, so a missing hook on them is correct — a
                                permanent badge on rows where it can never be acted
                                on is how the badge stops being read on the rows
                                where it matters (RidgeWill/auth#236). */}
                            {needsClosureHook(a) ? (
                              <Badge tone="warn">no closure hook</Badge>
                            ) : null}
                          </span>
                        </Td>
                        <Td>
                          {/* An app with no grants is registered and reachable by
                              nobody. A legitimate state, and one that looks
                              identical to a working app in a list that renders an
                              empty set as a blank cell. */}
                          {a.groups.length === 0 ? (
                            <Badge tone="warn">nobody</Badge>
                          ) : (
                            <span style={{ display: 'inline-flex', gap: '.3rem', flexWrap: 'wrap' }}>
                              {a.groups.map((g) => (
                                <Badge key={g}>{g}</Badge>
                              ))}
                            </span>
                          )}
                        </Td>
                        {anyHealth ? (
                          <Td>
                            {!a.healthCheckUrl ? (
                              <span style={{ color: C.muted, fontSize: '.8rem' }}>not configured</span>
                            ) : results.get(a.id) ? (
                              <HealthState
                                state={results.get(a.id)!.state}
                                status={results.get(a.id)!.status}
                                latencyMs={results.get(a.id)!.latencyMs}
                                code={results.get(a.id)!.code}
                              />
                            ) : (
                              <span style={{ color: C.muted, fontSize: '.8rem' }}>not checked</span>
                            )}
                          </Td>
                        ) : null}
                        <Td>
                          <Menu label={`Actions for ${a.name}`}>
                            {(close) => (
                              <MenuItem
                                href={`#/applications/${encodeURIComponent(a.slug)}`}
                                onClick={close}
                              >
                                Open details
                              </MenuItem>
                            )}
                          </Menu>
                        </Td>
                      </tr>
                    ))}
                  </Table>
                )}

              </>
            );
          }}
        </AsyncBoundary>
      </Panel>

      {adding ? (
        <AddApplication
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            reload();
          }}
        />
      ) : null}
    </Screen>
  );
}

/** "3 ok, 1 failing, 1 refused" — a sentence rather than a colour, because the
 *  summary is read before anybody looks at the column. */
function summarise(report: HealthReport): string {
  if (report.configured === 0) return 'nothing configured';
  const by = (state: string) => report.results.filter((r) => r.state === state).length;
  return [
    [by('ok'), 'ok'],
    [by('failing'), 'failing'],
    [by('unreachable'), 'no answer'],
    [by('refused'), 'refused'],
  ]
    .filter(([n]) => (n as number) > 0)
    .map(([n, label]) => `${count(n as number)} ${label}`)
    .join(', ');
}

function AddApplication({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const known = useAsync(() => adminApi.securityGroups());
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [status, setStatus] = useState<string>('planning');
  const [groups, setGroups] = useState<string[]>([]);
  const [ticketPrefix, setTicketPrefix] = useState('');
  const prefix = cleanTicketPrefix(ticketPrefix);
  const action = useAction();

  return (
    <Modal
      title="Register an application"
      onClose={onClose}
      width={560}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            disabled={action.running || !slug.trim() || !name.trim() || ticketPrefixProblem(prefix) !== null}
            onClick={() =>
              void action
                .run(async () => {
                  await adminApi.createApplication({
                    slug: slug.trim(),
                    name: name.trim(),
                    description: description.trim() || undefined,
                    platform: formatPlatforms(platforms) || undefined,
                    status,
                    ticketPrefix: prefix || null,
                    groups: groups.length ? groups : undefined,
                  });
                  return 'Registered.';
                })
                .then(onCreated)
            }
          >
            {action.running ? 'Registering…' : 'Register'}
          </Button>
        </ModalActions>
      }
    >
      <OutcomeNote outcome={action.outcome} />
      <Field
        label="Slug"
        hint="PERMANENT. It appears in token audiences and in the scope of any agreement attached to this app, neither of which is a foreign key. A rename would orphan them silently, so there is no rename."
      >
        <input
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          style={inputStyle}
          placeholder="keyodo"
        />
      </Field>
      <Field label="Name">
        <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
      </Field>
      <Field label="Description">
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          style={inputStyle}
        />
      </Field>
      <Field label="Platforms" hint="Where it runs. Tick every one that applies.">
        <PlatformGrid picked={platforms} onChange={setPlatforms} />
      </Field>
      <Field label="Status">
        <Select
          block
          label="Status"
          value={status}
          onChange={setStatus}
          options={STATUSES.map((v) => ({ value: v, label: v }))}
        />
      </Field>
      <TicketPrefixField value={ticketPrefix} onChange={setTicketPrefix} />
      <Field
        label="Reachable by"
        hint="Security groups, granted in the same step — registering an app and letting nobody in is the common mistake, because the second call is the one that gets forgotten. Basic Users is everybody."
      >
        <AsyncBoundary state={known.state}>
          {(d) => (
            <GrantCheckboxes
              all={d.groups.map((g) => g.slug)}
              labels={Object.fromEntries(d.groups.map((g) => [g.slug, `${g.name} (${g.members})`]))}
              picked={groups}
              onChange={setGroups}
            />
          )}
        </AsyncBoundary>
      </Field>
    </Modal>
  );
}

const rowLink = {
  fontWeight: 600,
  color: C.brand,
  textDecoration: 'none',
};
